/**
 * Admin Broadcast Screen
 * Integrated broadcast messaging for the main webapp
 * Supports mass broadcasts and individual user messages
 */

import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { api } from '../../services/api';

type MessageMode = 'broadcast' | 'individual';

export function AdminBroadcast() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<MessageMode>('broadcast');
  const [message, setMessage] = useState('');
  const [targetUsers, setTargetUsers] = useState<'all' | 'vip' | 'active'>('all');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [individualUserId, setIndividualUserId] = useState('');

  const sendBroadcast = async () => {
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    if (message.length > 4000) {
      alert('Message is too long. Maximum 4000 characters allowed.');
      return;
    }

    if (mode === 'individual') {
      if (!individualUserId.trim()) {
        alert('Please enter a Telegram User ID');
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to send this message to user ${individualUserId}?`
      );
      if (!confirmed) return;

      try {
        setLoading(true);
        const response = await api.sendBroadcast(message, [parseInt(individualUserId)]);
        setResult(response.data.summary);
        setMessage('');
        setIndividualUserId('');
        alert('Message sent successfully!');
      } catch (err: any) {
        alert('Failed to send message: ' + (err.response?.data?.error?.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to send this message to ${targetUsers} users?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await api.sendBroadcast(message, targetUsers);
      
      setResult(response.data.summary);
      setMessage('');
      alert('Broadcast sent successfully!');
    } catch (err: any) {
      alert('Failed to send broadcast: ' + (err.response?.data?.error?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getTargetIcon = () => {
    switch (targetUsers) {
      case 'vip': return '👑';
      case 'active': return '⚡';
      default: return '👥';
    }
  };

  const getTargetDescription = () => {
    switch (targetUsers) {
      case 'vip': return 'VIP users only';
      case 'active': return 'Active users (logged in within 7 days)';
      default: return 'All registered users';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      paddingBottom: '80px',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        textAlign: 'center',
        background: 'rgba(10, 22, 40, 0.8)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{ fontSize: '24px' }}>💬</div>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 700,
          margin: 0,
        }}>
          {t('admin.broadcast')}
        </h1>
      </div>

      {/* Broadcast Form */}
      <div style={{
        margin: '20px',
        background: 'rgba(10, 22, 40, 0.6)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(59, 130, 246, 0.2)',
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '20px',
        }}>
          Send Message to Users
        </h3>

        {/* Mode Toggle */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '20px' }}>
          <button
            onClick={() => setMode('broadcast')}
            style={{
              padding: '10px',
              borderRadius: '10px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              color: '#fff',
              background: mode === 'broadcast' ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)' : 'rgba(255,255,255,0.1)',
            }}
          >
            📢 Mass Broadcast
          </button>
          <button
            onClick={() => setMode('individual')}
            style={{
              padding: '10px',
              borderRadius: '10px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              color: '#fff',
              background: mode === 'individual' ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' : 'rgba(255,255,255,0.1)',
            }}
          >
            👤 Individual Message
          </button>
        </div>

        {mode === 'individual' ? (
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '8px',
            }}>
              Telegram User ID
            </label>
            <input
              type="text"
              value={individualUserId}
              onChange={(e) => setIndividualUserId(e.target.value)}
              placeholder="Enter Telegram User ID (e.g., 123456789)"
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '12px',
                color: '#fff',
                fontSize: '14px',
              }}
            />
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
              You can find User IDs in the Users management section
            </div>
          </div>
        ) : (
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            marginBottom: '12px',
          }}>
            Target Audience
          </label>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="target"
                value="all"
                checked={targetUsers === 'all'}
                onChange={(e) => setTargetUsers(e.target.value as any)}
                style={{ accentColor: '#3b82f6' }}
              />
              <span style={{ fontSize: '16px' }}>👥</span>
              All Users
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="target"
                value="vip"
                checked={targetUsers === 'vip'}
                onChange={(e) => setTargetUsers(e.target.value as any)}
                style={{ accentColor: '#3b82f6' }}
              />
              <span style={{ fontSize: '16px' }}>👑</span>
              VIP Users
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="target"
                value="active"
                checked={targetUsers === 'active'}
                onChange={(e) => setTargetUsers(e.target.value as any)}
                style={{ accentColor: '#3b82f6' }}
              />
              <span style={{ fontSize: '16px' }}>⚡</span>
              Active Users
            </label>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            padding: '8px 12px', 
            background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '8px',
            fontSize: '14px',
            color: '#ccc'
          }}>
            <span style={{ fontSize: '16px' }}>{getTargetIcon()}</span>
            {getTargetDescription()}
          </div>
        </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            marginBottom: '8px',
          }}>
            Message ({message.length}/4000 characters)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your broadcast message here..."
            maxLength={4000}
            style={{
              width: '100%',
              minHeight: '120px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '12px',
              color: '#fff',
              fontSize: '14px',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ 
          padding: '12px', 
          background: 'rgba(255, 193, 7, 0.1)', 
          border: '1px solid rgba(255, 193, 7, 0.3)', 
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          <strong>⚠️ Important:</strong> This message will be sent to all selected users via Telegram bot. 
          Make sure your message is appropriate and follows Telegram's terms of service.
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setMessage('');
              setResult(null);
            }}
            disabled={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '12px 20px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Clear
          </button>
          
          <button
            onClick={sendBroadcast}
            disabled={loading || !message.trim()}
            style={{
              background: loading || !message.trim() 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading || !message.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '16px' }}>📤</span>
            {loading ? 'Sending...' : 'Send Broadcast'}
          </button>
        </div>
      </div>

      {/* Broadcast Results */}
      {result && (
        <div style={{
          margin: '20px',
          background: 'rgba(10, 22, 40, 0.6)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 600,
            marginBottom: '20px',
          }}>
            Broadcast Results
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
          }}>
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: '#3b82f6',
                marginBottom: '4px',
              }}>
                {result.totalUsers}
              </div>
              <div style={{ fontSize: '14px', color: '#ccc' }}>Target Users</div>
            </div>
            
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: '#10b981',
                marginBottom: '4px',
              }}>
                {result.successCount}
              </div>
              <div style={{ fontSize: '14px', color: '#ccc' }}>Sent Successfully</div>
            </div>
            
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: 700,
                color: '#ef4444',
                marginBottom: '4px',
              }}>
                {result.failureCount}
              </div>
              <div style={{ fontSize: '14px', color: '#ccc' }}>Failed</div>
            </div>
          </div>

          {result.errors && result.errors.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ color: '#ef4444', marginBottom: '10px', fontSize: '16px' }}>Errors:</h4>
              <ul style={{ color: '#ef4444', fontSize: '14px', paddingLeft: '20px' }}>
                {result.errors.map((error: string, index: number) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}